import { Field, Radio, RadioGroup } from '@headlessui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { Glyphs } from '../../glyphs'
import { Tooltip } from '../../tooltip'

export type WidgetSelectGridHelpers = {
	className: string
}

export type WidgetSelectGridItem<T extends string> = {
	id: T
	render: (helpers: WidgetSelectGridHelpers) => ReactNode
	label: string
}

export type WidgetSelectGridProps<T extends string> = {
	value: T | null
	onChange: (value: T) => void
	items: Array<WidgetSelectGridItem<T>>
	/**
	 * Where an item's label goes. `'tooltip'` lets the thumbnails speak for themselves, which
	 * is the point of a swatch grid — and it keeps a long label from wrapping inside the 48px
	 * track, which looks especially lopsided when there are only one or two items.
	 */
	labels?: 'below' | 'tooltip'
}

export function WidgetSelectGrid<T extends string>(
	{ value, onChange, items, labels = 'below' }: WidgetSelectGridProps<T>,
) {
	const itemClassName = 'block size-12 rounded-lg ring-1 relative'
	const selectedItemClassName = 'ring-purple-400'
	const unselectedItemClassName = 'ring-grey-200 active:ring-purple-400'

	const selectedItemAdditionalChildren = (
		<div className="absolute top-1 right-1 text-purple-600">
			<Glyphs.micro.BoldCheckIcon />
		</div>
	)

	return (
		<RadioGroup
			value={value}
			onChange={onChange}
			className="grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-2"
		>
			{items.map(({ id, render, label }) => {
				// The label below is a plain div rather than a Headless UI `Label`, so it is not
				// associated with the radio; the name has to be set explicitly in both modes.
				const radio = (
					<Radio
						as="div"
						value={id}
						aria-label={label || undefined}
						className={ctl(`
                                relative cursor-pointer
								grid
                                ${itemClassName}
                                ${value === id ? selectedItemClassName : unselectedItemClassName}
                            `)}
					>
						{render({
							className: 'rounded-lg',
						})}
						{value === id && selectedItemAdditionalChildren}
					</Radio>
				)

				return (
					<Field key={id}>
						{labels === 'tooltip' && label ? <Tooltip content={label}>{radio}</Tooltip> : radio}
						{labels === 'below' && label && <div className="text-xs text-grey-500 mt-1">{label}</div>}
					</Field>
				)
			})}
		</RadioGroup>
	)
}
